"use client";

import { useCallback, useMemo, useState } from "react";
import { useTranslations } from "@zapier/i18n";
import {
  Template,
  useChangeTemplateOwner,
  useQuerySearchMembers,
} from "@zapier/client";
import { ChangeOwnerModal, type ChangeOwnerUser } from "@zapier/shared";
import { AssetTypes, useNotification } from "@zapier/utils";

interface Props {
  template: Template | undefined;
  isOpen: boolean;
  onCloseModal: () => void;
}

export function TemplateChangeOwnerModal({
  template,
  isOpen,
  onCloseModal,
}: Props) {
  const t = useTranslations("template-change-owner-modal");
  const { createNotification } = useNotification();
  const [userSearch, setUserSearch] = useState("");

  const { members, isLoading: isUsersLoading } = useQuerySearchMembers({
    search: userSearch,
    enabled: isOpen,
  });

  const users: ChangeOwnerUser[] = useMemo(
    () =>
      members.map((member) => ({
        id: member.id,
        name: member.fullName,
        email: member.email,
        photoUrl: member.photoUrl,
      })),
    [members],
  );

  const { changeTemplateOwner, isPending: isSubmitting } =
    useChangeTemplateOwner();

  const onSearch = useCallback((search: string) => {
    setUserSearch(search);
  }, []);

  const currentOwnerId = useMemo(
    () =>
      template?.owner &&
      "id" in template.owner &&
      template.owner.id !== undefined
        ? Number(template.owner.id)
        : undefined,
    [template],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setUserSearch("");
        onCloseModal();
      }
    },
    [onCloseModal],
  );

  const onSubmit = useCallback(
    async (ownerId: string) => {
      if (!ownerId || !template) return;
      const selectedUser = users.find((u) => String(u.id) === ownerId);
      await changeTemplateOwner(
        {
          templateId: template.id,
          ownerId,
          newOwnerFullName: selectedUser?.name ?? "",
          newOwnerPhotoUrl: selectedUser?.photoUrl ?? undefined,
        },
        {
          onSuccess: () => {
            createNotification({
              message: t("notifications.change-owner-success"),
              type: "success",
            });
            // Reuse handleOpenChange so userSearch is cleared (onCloseModal alone leaves stale search)
            handleOpenChange(false);
          },
          onError: (error) => {
            createNotification({
              message: t("notifications.change-owner-error", {
                errorMessage:
                  error instanceof Error ? error.message : String(error),
              }),
              type: "error",
            });
          },
        },
      );
    },
    [
      changeTemplateOwner,
      createNotification,
      handleOpenChange,
      t,
      template,
      users,
    ],
  );

  if (!template || !isOpen) {
    return;
  }

  return (
    <ChangeOwnerModal
      id={template.id}
      title={template.name ?? ""}
      currentOwnerId={currentOwnerId}
      isOpen={isOpen}
      setIsOpen={handleOpenChange}
      users={users}
      assetType={AssetTypes.Template}
      onSearch={onSearch}
      isUsersLoading={isUsersLoading}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
    />
  );
}
